import React, { useRef, useEffect, useContext, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search } from 'react-feather';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import ThemeToggleButton from '../ThemeToggleButton';
import { CartContext } from '../CartContext';
import { AuthContext } from '../AuthContext';
import { SearchContext } from '../SearchContext';
import { useDebounce } from '../hooks/useDebounce';
import allProducts from '../products.json';
import { useClickOutside } from '../hooks/useClickOutside';
import FlyToCartAnimator from './FlyToCartAnimator';

const navLinks = [
  { path: '/', text: 'Home' },
  { path: '/shop', text: 'Shop' },
  { path: '/blog', text: 'Blog' },
  { path: '/about', text: 'About' },
  { path: '/contact', text: 'Contact' },
];

const navContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07, // Time between each nav link animating in
      delayChildren: 0.1,    // Delay before the first nav link starts
    },
  },
};

const navItemVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { ease: [0.4, 0, 0.2, 1], duration: 0.4 } },
};

const Header = () => {
  const { cartItemCount } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { searchQuery, setSearchQuery } = useContext(SearchContext);
  const navigate = useNavigate();
  const cartRef = useRef(null);
  const searchRef = useRef(null);
  const controls = useAnimation();
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const isLoading = searchQuery !== debouncedSearchQuery;

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setScrolled(y > 12);
    };
    
    // Live search filtering
    if (isSearchOpen && debouncedSearchQuery.trim() !== '') {
      const filtered = allProducts
        .filter(product =>
          product.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
        )
        .slice(0, 5); // Limit to 5 results
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      // Ensure body scroll is re-enabled on component unmount
      document.body.style.overflow = '';
    };
  }, [debouncedSearchQuery, isSearchOpen]);

  // Effect to lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
  }, [isMenuOpen]);

  // Close search when clicking outside
  useClickOutside(searchRef, () => setIsSearchOpen(false));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('/shop');
      setIsSearchOpen(false);
    }
  };
  return (
    <motion.header
      className={`site-header ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <FlyToCartAnimator cartRef={cartRef} cartControls={controls} />
      <div className="container">
        <nav className="main-nav" role="navigation" aria-label="Main Navigation">
          <div className="nav-section nav-section--left">
            <NavLink className="navbar-brand" to="/"><strong>KONUKKO</strong></NavLink>
          </div>

          <div className="nav-section nav-section--center">
            <AnimatePresence mode="wait" initial={false}>
              {isSearchOpen ? (
                <motion.div
                  key="search"
                  ref={searchRef}
                  className="header-search-container"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                  <form onSubmit={handleSearchSubmit} className="header-search-form">
                    <input type="text" className="header-search-input" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus />
                  </form>
                  {searchQuery.trim() !== '' && !isLoading && (
                    <motion.div
                      className="search-results-dropdown"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      {searchResults.length > 0 ? (
                        searchResults.map((product) => (
                            <Link
                              key={product.id}
                              to={`/product/${product.id}`}
                              className="search-result-item"
                              onClick={() => setIsSearchOpen(false)}
                            >
                              <img src={product.imageUrl} alt={product.name} className="search-result-image" />
                              <span className="search-result-name">{product.name}</span>
                            </Link>
                        ))
                      ) : (
                        <div className="search-no-results">
                          No products found for "{searchQuery}".
                        </div>
                      )}
                    </motion.div>
                  )}
                  {isLoading && <div className="search-loading-spinner"></div>}

                </motion.div>
              ) : (
                <motion.ul
                  key="nav"
                  className={`navbar-nav ${isMenuOpen ? 'is-open' : ''}`}
                  variants={navContainerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  role="menubar"
                >
                  {navLinks.map((link, idx) => (
                    <motion.li key={link.path} variants={navItemVariants} className="nav-item" role="none">
                      <NavLink className="nav-link" to={link.path} role="menuitem" onClick={() => setIsMenuOpen(false)}>{link.text}</NavLink>
                    </motion.li>
                  ))}
                  <div className="mobile-menu-actions">
                    {user ? <NavLink className="nav-link" to="/profile" onClick={() => setIsMenuOpen(false)}>Profile</NavLink> : <NavLink className="nav-link" to="/login" onClick={() => setIsMenuOpen(false)}>Login</NavLink>}
                    <ThemeToggleButton />
                  </div>
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <div className="nav-section nav-section--right">
            <div className="nav-actions">
              <button className="btn--icon" onClick={() => setIsSearchOpen(!isSearchOpen)} aria-label="Open search">
                {isSearchOpen ? <X size={20} /> : <Search size={20} />}
              </button>

              {/* Desktop-only actions */}
              <div className="desktop-actions">
                {user ? (
                  <NavLink className="btn--icon" to="/profile" aria-label="View Profile">
                    <User size={20} />
                  </NavLink>
                ) : (
                  <NavLink className="btn--icon" to="/login" aria-label="Login">
                    <User size={20} />
                  </NavLink>
                )}
              </div>

              <Link ref={cartRef} className="btn--icon position-relative" to="/cart" aria-label="View Shopping Cart">
                <motion.div animate={controls} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ShoppingCart size={20} />
                </motion.div>
                {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
              </Link>

              <div className="desktop-actions">
                <ThemeToggleButton />
              </div>

              <button className="mobile-menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? "Close menu" : "Open menu"} aria-expanded={isMenuOpen}>
                <AnimatePresence mode="wait">
                  <motion.div key={isMenuOpen ? 'x' : 'menu'} initial={{ opacity: 0, rotate: -90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 0, rotate: 90 }} transition={{ duration: 0.2 }}>
                    {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                  </motion.div>
                </AnimatePresence>
              </button>
            </div>
          </div>
        </nav>

      </div>
    </motion.header>
  );
};

export default Header;
